import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Sparkles, ClipboardList, Pencil } from 'lucide-react';
import { needsAnalysisAPI } from '@/lib/api';

/**
 * Overview card showing the lead's saved needs analysis. "Edit" opens the
 * drawer. `refreshKey` lets the parent force a reload after a save.
 */
export default function NeedsAnalysisCard({ leadId, onEdit, refreshKey, staticData = null, readOnly = false }) {
  const [data, setData] = useState(staticData);
  const [loaded, setLoaded] = useState(!!staticData);

  useEffect(() => {
    if (staticData) { setData(staticData); setLoaded(true); return; }
    if (!leadId) return;
    needsAnalysisAPI.getForLead(leadId)
      .then((r) => setData(r.data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, [leadId, refreshKey, staticData]);

  if (!loaded) return null;
  const answered = (data?.answers || []).filter((a) => (a.answer || '').trim());
  if (readOnly && answered.length === 0) return null;

  return (
    <Card data-testid="needs-analysis-card">
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-base font-heading flex items-center gap-2">
          <ClipboardList size={16} /> Needs Analysis
          {data?.product_label && (
            <span className="text-xs font-normal text-slate-400">· {data.product_label}</span>
          )}
        </CardTitle>
        {!readOnly && (
          <Button variant="outline" size="sm" onClick={onEdit} data-testid="na-edit-btn">
            <Pencil size={13} className="mr-1" /> {answered.length ? 'Edit' : 'Start'}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        {answered.length === 0 ? (
          <p className="text-sm text-slate-400 italic" data-testid="na-empty">
            No needs analysis yet. Click Start to capture what this lead needs.
          </p>
        ) : (
          <div className="space-y-2">
            <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
              {answered.map((a) => (
                <div key={a.id} className="text-sm">
                  <dt className="text-xs text-slate-400">{a.question}</dt>
                  <dd className="text-slate-800 whitespace-pre-wrap">{a.answer}</dd>
                </div>
              ))}
            </dl>
            {data?.ai_summary && (
              <div className="mt-3 rounded-md border border-indigo-200 bg-indigo-50 p-3" data-testid="na-card-ai">
                <p className="text-xs font-semibold text-indigo-900 mb-1 flex items-center gap-1">
                  <Sparkles size={12} /> AI Recommendation
                </p>
                <p className="text-sm text-indigo-900 whitespace-pre-wrap">{data.ai_summary}</p>
                {(data.cross_sell || []).length > 0 && (
                  <div className="mt-2 pt-2 border-t border-indigo-200" data-testid="na-card-cross-sell">
                    <p className="text-[11px] font-semibold text-indigo-900 mb-1">Cross-sell opportunities</p>
                    <div className="flex flex-wrap gap-1">
                      {data.cross_sell.map((p) => (
                        <span key={p} className="text-[11px] bg-amber-100 text-amber-800 rounded-full px-2 py-0.5">{p}</span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
